/**
 * SSE contract v2 for POST /chat: the JSDoc mirror of backend/core/stream_contract.py
 * (design section 3a). Keep the two in step; the backend Pydantic models are the source of truth.
 *
 * Wire format of one event (comment lines such as ": ping" are heartbeats):
 *
 *     id: <seq, 1-based>
 *     event: <type>
 *     data: <single-line JSON whose "type" equals the event name>
 *
 * Order:
 *     meta -> status* -> sources (exactly once, may be []) -> status(generating) -> delta*
 *       -> answer_end -> status(suggesting)? -> followups? -> done
 * `error` is terminal and may arrive anywhere; no `done` follows it.
 * POST is not idempotent, so the client never reconnects on its own; it offers Retry instead.
 */

export const PROTOCOL = 2;

/** Event names (also the `type` field of every payload). */
export const EVENTS = Object.freeze({
  META: 'meta',
  STATUS: 'status',
  SOURCES: 'sources',
  DELTA: 'delta',
  ANSWER_END: 'answer_end',
  FOLLOWUPS: 'followups',
  DONE: 'done',
  ERROR: 'error',
});

/** Stable stage codes of `status` events, in pipeline order. */
export const STAGES = Object.freeze([
  'understanding',
  'searching',
  'reading',
  'generating',
  'finalizing',
  'suggesting',
  // replies without a manual search: small talk, memory questions, the "which vehicle?" question
  'chatting',
  'recalling',
  'clarifying',
]);

/** Server error codes, plus the client-only codes the SSE client synthesizes. */
export const ERROR_CODES = Object.freeze({
  UNAUTHORIZED: 'unauthorized',
  VALIDATION: 'validation',
  RETRIEVAL_FAILED: 'retrieval_failed',
  GENERATION_FAILED: 'generation_failed',
  TIMEOUT: 'timeout',
  INTERNAL: 'internal',
  // client-only: the connection dropped or the stream ended without `done`/`error`
  NETWORK: 'network',
});

/**
 * @typedef {Object} Source
 * @property {number} id                 1-based, unique per message; what `[n]` in the text refers to
 * @property {string} page_key           "<manual_name>#<page_number>"
 * @property {string} manual_name
 * @property {string} manual_label       short display label, e.g. "Vol.8 Electrical"
 * @property {number} page_number        0-based (the index convention)
 * @property {string} page_label         1-based for people, e.g. "p.254"
 * @property {?string} printed_page      printed page number when known, e.g. "54-246"
 * @property {string} section
 * @property {string} section_title
 * @property {string} title
 * @property {string} snippet            <= 240 chars
 * @property {string} model
 * @property {string} year
 * @property {boolean} figure            a drawing page to show large
 * @property {string} image_path         relative to the API base, URL-encoded: "pages/<manual>/<page>.jpg"
 * @property {string} thumb_path         "pages/<manual>/<page>.jpg?w=360"
 * @property {number} score
 * @property {string} [image_url]        client-only: an absolute/data URL that overrides image_path (legacy history)
 */

/**
 * @typedef {Object} MetaEvent
 * @property {'meta'} type
 * @property {2} protocol
 * @property {string} conversation_id
 * @property {string} message_id
 * @property {'deep'|'quick'} mode
 * @property {string} language
 * @property {?string} model
 * @property {?string} model_label
 * @property {?string} year
 * @property {string} created_at         ISO 8601
 */

/**
 * @typedef {Object} StatusEvent
 * @property {'status'} type
 * @property {'understanding'|'searching'|'reading'|'generating'|'finalizing'|'suggesting'|'chatting'|'recalling'|'clarifying'} stage
 * @property {string} label              localized
 * @property {Object<string, *>} [detail] e.g. {pages: 6}
 */

/**
 * @typedef {Object} SourcesEvent
 * @property {'sources'} type
 * @property {boolean} figures_requested
 * @property {Source[]} sources          <= 12 items, ids 1..N in context order
 */

/**
 * @typedef {Object} DeltaEvent
 * @property {'delta'} type
 * @property {string} text               raw markdown, appended as-is (may hold partial or invalid [n])
 */

/**
 * @typedef {Object} Notice
 * @property {'disclaimer'|'info'} kind
 * @property {string} text
 */

/**
 * @typedef {Object} AnswerEndEvent
 * @property {'answer_end'} type
 * @property {string} text               authoritative final GFM markdown; replaces the streamed buffer
 * @property {number[]} cited_ids        in order of first appearance
 * @property {boolean} grounded
 * @property {'stop'|'length'|'stopped'|'error'} finish_reason
 * @property {?Notice} notice
 */

/**
 * @typedef {Object} Suggestion
 * @property {string} text
 * @property {string} type
 * @property {'send_message'|'append_to_input'} action
 * @property {string} value
 * @property {number} [priority]
 * @property {?string} [icon]
 * @property {?string} [prompt]
 * @property {?string} [facet]
 */

/**
 * @typedef {Object} FollowupsEvent
 * @property {'followups'} type
 * @property {?string} prompt
 * @property {Suggestion[]} items
 */

/**
 * @typedef {Object} DoneEvent
 * @property {'done'} type
 * @property {string} message_id
 * @property {string} conversation_id
 * @property {boolean} saved
 * @property {Object<string, number>} timing_ms  e.g. {retrieval, first_token, total}
 * @property {boolean} [cached]           true when the answer was replayed from the answer cache (absent otherwise)
 */

/**
 * @typedef {Object} ErrorEvent
 * @property {'error'} type
 * @property {'unauthorized'|'validation'|'retrieval_failed'|'generation_failed'|'timeout'|'internal'|'network'} code
 * @property {string} message            localized, safe to show
 * @property {boolean} retryable
 * @property {boolean} partial           an answer was partially streamed before the error
 */

/**
 * History contract: GET /history/conversation/{id}?include_images=false returns each row with
 * `answer` (v2 rows as stored; legacy rows adapted server-side with protocol 1).
 *
 * @typedef {Object} HistoryAnswer
 * @property {1|2} protocol
 * @property {string} text
 * @property {Source[]} sources
 * @property {number[]} cited_ids
 * @property {?Notice} notice
 * @property {'deep'|'quick'} [mode]
 * @property {'complete'|'stopped'} [status]
 */

/** True when `value` looks like a v2 event payload. */
export function isEvent(value) {
  return Boolean(value) && typeof value === 'object' && typeof value.type === 'string';
}
